import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';

import CloseIcon from '@material-ui/icons/Close';

import Loading from 'app/components/loaders/Loading';
import states from 'app/utils/States';

import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    }
}));

const initialValues = {
    id: 0,
    email: "",
    phone_mobile: "",
    prefix: "",
    first: "",
    middle: "",
    last: "",
    suffix: "",
    address_one: "",
    address_two: "",
    city: "",
    state: "",
    zip: "",
    full_name: "",
}

export default function EditGuestDialog(props) {

    const [ values, setValues ] = useState(initialValues);

    const [ loading, setLoading ] = useState(false);
    const [ errors, setErrors ] = useState({});

    const { open, user, onClose, onSave, onDelete, onClosed, guest, eventID } = props;

    const classes = useStyles();
    const api = useApi();

    useEffect(() => {

        if (user) setValues(user);

        setErrors({});

    }, [user]);

    // Setter Functions ------------------------------------------

    const handleChange = name => e => {

        const target = e.target;
        const value = (target.type === 'checkbox' ? target.checked : target.value);

        if (isError(name)) delete errors[name];

        setValues({ ...values, [name]: value });
    };

    // Post functions ------------------------------------------

    const postUser = () => {

        setLoading(true);

        const data = {...values};

        data.event_id = eventID;

        api.post(`/admin/guest/${data.id}`, data)
            .then(() => onSave())
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).finally(() => setLoading(false));
    };

    /*
    const deleteUser = () => {

        setLoading(true);

        api.delete('/admin/ticket/' + values.id).then(res => {
            onDelete();
        }).catch(error => {
            console.log(error.response);
        }).then( () => {
            setLoading(false);
        });
    }
    */

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    const clearValues = () => {
        setValues(initialValues);
    };

    const onExited = () => {
        clearValues();
        onClosed();
    }

    return (
        <Dialog
            aria-labelledby="edit-guest-dialog-title"
            open={open}
            aria-describedby="edit-guest-dialogue-description"
            fullWidth
            maxWidth="sm"
            onExited={onExited}
            >
            <Loading isLoading={loading}/>
            <DialogTitle id="edit-guest-dialog-title">
                <span>{(values.id > 0) ? "Edit " + values.full_name : "New Guest"}</span>
                <IconButton aria-label="Cancel" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="body1" color="textSecondary" component="legend">
                    Guest Details
                </Typography>
                <TextField
                    id="input-prefix"
                    label="Prefix"
                    value={values.prefix}
                    onChange={handleChange('prefix')}
                    fullWidth
                    error={isError('prefix')}
                    helperText={getErrorMessage('prefix')}
                    margin="normal"
                    variant="outlined"
                />
                <TextField
                    id="input-first"
                    label="First"
                    value={values.first}
                    onChange={handleChange('first')}
                    fullWidth
                    required
                    error={isError('first')}
                    helperText={getErrorMessage('first')}
                    margin="normal"
                    variant="outlined"
                />
                <TextField
                    id="input-middle"
                    label="Middle"
                    value={values.middle}
                    onChange={handleChange('middle')}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                />
                <TextField
                    id="input-last"
                    label="Last"
                    value={values.last}
                    onChange={handleChange('last')}
                    fullWidth
                    required
                    error={isError('last')}
                    helperText={getErrorMessage('last')}
                    margin="normal"
                    variant="outlined"
                />
                <TextField
                    id="input-suffix"
                    label="Suffix"
                    value={values.suffix}
                    onChange={handleChange('suffix')}
                    fullWidth
                    error={isError('suffix')}
                    helperText={getErrorMessage('suffix')}
                    variant='outlined'
                    className={classes.marginBottom}
                />
                <Typography variant="body1" color="textSecondary">
                    Guest Home Address
                </Typography>
                <TextField
                    id="input-address-one"
                    label="Address One"
                    value={values.address_one}
                    onChange={handleChange('address_one')}
                    fullWidth
                    error={isError('address_one')}
                    helperText={getErrorMessage('address_one')}
                    margin="normal"
                    variant="outlined"
                />
                <TextField
                    id="input-address-two"
                    label="Address Two"
                    value={values.address_two}
                    onChange={handleChange('address_two')}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                />
                <TextField
                    id="input-city"
                    label="City"
                    value={values.city}
                    onChange={handleChange('city')}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    error={isError('address_one')}
                    helperText={getErrorMessage('address_one')}
                />
                <Grid container spacing={3} className={classes.marginBottom} >
                    <Grid item xs={12} sm={4}>
                        <TextField
                            id="input-state"
                            select
                            label="State"
                            value={values.state}
                            onChange={handleChange('state')}
                            fullWidth
                            margin="normal"
                            variant="outlined"
                            error={isError('address_one')}
                            helperText={getErrorMessage('address_one')}
                            >
                            {states.map( state => {
                                return (
                                    <MenuItem key={state.abbreviation} value={state.abbreviation} >{state.name}</MenuItem>
                                )
                            })}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <TextField
                            id="input-zip"
                            label="ZIP"
                            value={values.zip}
                            onChange={handleChange('zip')}
                            fullWidth
                            margin="normal"
                            variant="outlined"
                            error={isError('address_one')}
                            helperText={getErrorMessage('address_one')}
                        />
                    </Grid>
                </Grid>
                <Typography variant="body1" color="textSecondary">
                    Guest Contact
                </Typography>
                <TextField
                    id="input-email"
                    label="Email"
                    type="email"
                    autoComplete="email"
                    value={values.email}
                    onChange={handleChange('email')}
                    fullWidth
                    margin="normal"
                    error={isError('email')}
                    helperText={getErrorMessage('email')}
                    variant="outlined"
                />
                <TextField
                    id="input-phone-mobile"
                    label="Mobile Phone"
                    value={values.phone_mobile}
                    onChange={handleChange('phone_mobile')}
                    fullWidth
                    margin="normal"
                    error={isError('phone_mobile')}
                    helperText={getErrorMessage('phone_mobile')}
                    variant="outlined"
                    />
            </DialogContent>
            <DialogActions>
                {/* values.id > 0 &&
                    <Button onClick={deleteTicket} color="secondary">
                        Delete
                    </Button>
                */}
                <Button onClick={postUser} color="primary" variant="contained">
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}
